
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

interface BillStatusDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  billId: string;
  action: 'paid' | 'cancel';
  isLoading?: boolean;
}

export function BillStatusDialog({ 
  isOpen, 
  onOpenChange, 
  onConfirm,
  billId,
  action,
  isLoading = false
}: BillStatusDialogProps) {
  const title = action === 'paid' ? 'Mark Bill as Paid' : 'Cancel Bill';
  const description = action === 'paid' 
    ? 'Are you sure you want to mark this bill as paid? This action cannot be undone.'
    : 'Are you sure you want to cancel this bill? This will also cancel the related subscription. This action cannot be undone.';
  const confirmText = action === 'paid' ? 'Yes, mark as paid' : 'Yes, cancel bill';
  const confirmClass = action === 'paid' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600';

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.removeProperty('pointer-events');
    };
  }, []);

  // Handle dialog state changes
  useEffect(() => {
    if (!isOpen) {
      // Add immediate cleanup
      document.body.style.removeProperty('pointer-events');
      // Add delayed cleanup to catch any race conditions
      const timeoutId = setTimeout(() => {
        document.body.style.removeProperty('pointer-events');
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  return (
    <AlertDialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!isLoading) {
          if (!open) {
            document.body.style.removeProperty('pointer-events');
          }
          onOpenChange(open);
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button
            onClick={onConfirm}
            className={confirmClass}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              confirmText
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
